/**
 * Shrink a photo before uploading it.
 *
 * ⚠ WHY THIS EXISTS. A modern phone camera produces a 4–6 MB JPEG. A school clerk on a rural
 * connection uploading a fuel bill twice a week per bus, across twelve buses, is uploading
 * hundreds of megabytes a month for images that will only ever be looked at on a screen. Worse,
 * a slow upload on a bad signal is how a clerk decides the feature is broken and stops using it.
 *
 * 1600px on the long edge at quality 0.72 lands a typical bill photo at 150–400 KB and stays
 * comfortably readable — a fuel bill has large printed digits, not fine print.
 *
 * ⚠ FAILS OPEN. If anything goes wrong — an unsupported format, a browser without canvas encoding,
 * an image too large to decode — the ORIGINAL file is returned and the upload proceeds. A photo
 * that uploads slowly is a far better outcome than a photo that cannot be uploaded at all, and the
 * server has its own 8 MB ceiling as the real limit.
 */
export async function compressImage(
  file: File,
  maxEdge = 1600,
  quality = 0.72,
): Promise<File> {
  // ⚠ PDFs and anything non-image pass straight through. A scanned PDF bill is legitimate and
  // must not be fed to a canvas.
  if (!file.type.startsWith('image/')) return file;

  // ⚠ HEIC (iPhone default in some settings) cannot be decoded by canvas in most browsers.
  // Returning the original lets the server store it; forcing it through here would produce a
  // blank white image, which is worse than a large file.
  if (file.type.includes('heic') || file.type.includes('heif')) return file;

  // Already small enough that re-encoding would cost quality for no benefit.
  if (file.size < 400 * 1024) return file;

  try {
    const bitmap = await createImageBitmap(file);

    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    );

    // ⚠ If the "compressed" result is bigger, keep the original. Re-encoding an already-optimised
    // JPEG can grow it, and shipping the larger file would be an own goal.
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], file.name.replace(/\.(png|webp|bmp|tiff?)$/i, '.jpg'), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}
